import React, { useEffect, useState } from 'react';
import GenericBadge from 'components/ui/Badge';
import { ContractQuoteDto } from 'api/contract';
import { downloadDocumentByStoredId } from 'services/documentsAPI';
import { contractsAPI } from 'services/swagger-api';
import { useContractInfosState } from '../ContractInfosContext';
import { GenericLoading } from 'components/ui/Loader';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { GenericColors } from 'enums/genericColors';

export const ContractQuotesOverview: React.FC = () => {
  const { contractId } = useContractInfosState()
  const [rows, setRows] = useState<ContractQuoteDto[]>()
  const { downloadQuoteFile } = useContractQuoteActions();
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    const getQuotes = async () => {
      try {
        setIsLoading(true)
        const response = await contractsAPI.getQuotes({ contractId })
        if (response) {
          setRows(response.quotes)
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des devis:', error)
      } finally {
        setIsLoading(false)
      }
    }
    getQuotes()
  }, [contractId])

  const handleQuoteClick = (fileId: string) => {
    downloadQuoteFile(fileId)
  };

  if (!rows || isLoading) {
    return (
      <div className="relative flex justify-center items-center p-8">
        <GenericLoading isParentSized />
      </div>
    );
  }

  return (
    <CardsSection title='Liste des devis'>
      <div>
        {/* Table Body */}
        <div className="">
          {rows.map((quote, index) => (
            <div
              key={`${quote.quoteIdentifier}-${index}`}
              className="border rounded-2xl bg-white grid grid-cols-12 gap-4 px-6 py-4"
            >
              {/* N°Devis */}
              <div className="col-span-5 flex items-center">
                <button
                  onClick={() => handleQuoteClick(quote.fileId)}
                  className="text-primary font-semibold hover:text-primary hover:underline transition-colors"
                >
                  {quote.quoteIdentifier}
                </button>
              </div>

              {/* Date */}
              <div className="col-span-2 flex items-center">
                <div className="text-gray-600">
                  <div className="text-sm">{formatDateToFrench(quote.creationDate)}</div>
                </div>
              </div>

              {/* Prime TTC */}
              <div className="col-span-2 flex items-center">
                <div className="font-semibold text-gray-900">
                  {quote.priceIncludingTax.toFixed(2).toString()} €
                </div>
              </div>

              {/* Statut */}
              <div className="col-span-2 flex items-center">
                <GenericBadge
                  label={quote.statusLabel}
                  color={quote.statusColor as GenericColors}
                  badgeType="sm-bold"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end">
                <Button
                  round
                  color='slate'
                  onClick={() => handleQuoteClick(quote.fileId)}
                >
                  <EyeIcon className="w-5 h-5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {rows.length === 0 && (
          <div className="px-6 py-12 text-center">
            <div className="text-gray-500 text-sm">
              Aucun devis trouvé
            </div>
          </div>
        )}
      </div>
    </CardsSection>
  );
};

// Hook utilitaire pour gérer les actions sur les devis
export const useContractQuoteActions = () => {
  const downloadQuoteFile = async (fileId: string) => {
    if (!fileId) {
      return;
    }
    try {
      const res = await downloadDocumentByStoredId(fileId);
      const blob = new Blob([res], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error downloading quote file:', error);
    }
  };

  return {
    downloadQuoteFile
  };
};

/**
 * Formate une date en format français (DD/MM/YYYY)
 */
function formatDateToFrench(date: Date): string {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

